export type PhaseKind = 'inhale' | 'holdIn' | 'exhale' | 'holdOut';

export interface Bilingual {
  no: string;
  en: string;
}

export interface PhaseGuidance {
  kind: PhaseKind;
  seconds: number;
  label: Bilingual;
  instruction: Bilingual;
}

export interface BeatConfig {
  carrierHz: number;
  beatHz: number;
  /** For the Void Breath: beat eases to this value during the settle phase. */
  settleBeatHz?: number;
}

export interface SettlePhase {
  seconds: number;
  instruction: Bilingual;
}

export interface Protocol {
  id: string;
  name: Bilingual;
  subtitle: Bilingual;
  durationLabel: Bilingual;
  phases: PhaseGuidance[];
  cycles?: number;
  durationSec?: number;
  settle?: SettlePhase;
  sound: BeatConfig;
  safety?: Bilingual;
  closingNote: Bilingual;
}

export interface Routine {
  id: 'morning' | 'evening';
  name: Bilingual;
  description: Bilingual;
  protocolIds: string[];
}

const IN: Bilingual = { no: 'Pust inn', en: 'Breathe in' };
const HOLD: Bilingual = { no: 'Hold', en: 'Hold' };
const OUT: Bilingual = { no: 'Pust ut', en: 'Breathe out' };
const EMPTY: Bilingual = { no: 'Hvil tom', en: 'Rest empty' };

export const PROTOCOLS: Record<string, Protocol> = {
  descent: {
    id: 'descent',
    name: { no: 'Nedstigningen', en: 'The Descent' },
    subtitle: {
      no: 'Åpner alfa-broen. Flytter hjernen fra beta mot alfa.',
      en: 'Opens the alpha bridge. Moves the brain from beta toward alpha.',
    },
    durationLabel: { no: '5 minutter', en: '5 minutes' },
    phases: [
      {
        kind: 'inhale',
        seconds: 4,
        label: IN,
        instruction: {
          no: 'Sakte, gjennom nesen. Utvid magen først, så brystet. Ingen tvang — la det være lett.',
          en: 'Slow, through the nose. Expand the belly first, then the chest. No forcing — ease matters.',
        },
      },
      {
        kind: 'holdIn',
        seconds: 4,
        label: HOLD,
        instruction: {
          no: 'Mild pause på toppen. Ikke anstrengt. Dette lar trykket skifte og forsterker utpusten.',
          en: 'Gentle retention at the top. Not forceful. This lets the pressure shift and amplifies the exhale.',
        },
      },
      {
        kind: 'exhale',
        seconds: 8,
        label: OUT,
        instruction: {
          no: 'Forlenget, gjennom munn eller nese. Dette 2:1-forholdet er den aktive variabelen — det roer hjertet.',
          en: 'Extended, through mouth or nose. This 2:1 ratio is the active variable — it calms the heart.',
        },
      },
    ],
    cycles: 8,
    settle: {
      seconds: 60,
      instruction: {
        no: 'Sitt i 60 sekunder i naturlig pust uten å styre. Legg merke til skiftet — dette er alfa som ankommer.',
        en: 'Sit for 60 seconds in natural breath without guiding. Notice the shift — this is alpha arriving.',
      },
    },
    sound: { carrierHz: 396, beatHz: 10 },
    closingNote: {
      no: 'Kvaliteten av ro du kjenner er ikke innbilning. Det er målbar parasympatisk dominans.',
      en: 'The ease you feel is not imagination. It is measurable parasympathetic dominance.',
    },
  },

  pranic: {
    id: 'pranic',
    name: { no: 'Den Praniske Forsterkeren', en: 'The Pranic Amplifier' },
    subtitle: {
      no: 'Kort sympatisk aktivering som kollapser i dyp parasympatisk tilbakeslag.',
      en: 'Brief sympathetic arousal that collapses into deep parasympathetic rebound.',
    },
    durationLabel: { no: '3–4 minutter', en: '3–4 minutes' },
    phases: [
      {
        kind: 'inhale',
        seconds: 1,
        label: IN,
        instruction: {
          no: 'Skarp, full, gjennom nesen. Fyll helt i ett drag — mage, bryst, kragebein.',
          en: 'Sharp, full, through the nose. Fill completely in one breath — belly, chest, clavicle.',
        },
      },
      {
        kind: 'holdIn',
        seconds: 10,
        label: HOLD,
        instruction: {
          no: 'Hold på toppen i 10 sekunder. Trekk lett inn bekkenbunn og nedre mage. Varme og prikking er ventet.',
          en: 'Retain at the top for 10 seconds. Lightly draw in the pelvic floor and lower belly. Warmth and tingling are expected.',
        },
      },
      {
        kind: 'exhale',
        seconds: 1,
        label: OUT,
        instruction: {
          no: 'Full slipp gjennom munnen.',
          en: 'Complete release through the mouth.',
        },
      },
      {
        kind: 'holdOut',
        seconds: 5,
        label: EMPTY,
        instruction: {
          no: 'Hold tom i 5 sekunder før neste innpust. Den tomme pausen er der tilbakeslaget skjer — tilstanden ankommer ofte her.',
          en: 'Hold empty for 5 seconds before the next inhale. The empty pause is where the rebound occurs — the state often arrives here.',
        },
      },
    ],
    cycles: 10,
    sound: { carrierHz: 417, beatHz: 12 },
    safety: {
      no: 'Ikke bruk ved hjerte-/karsykdom, graviditet eller epilepsi. Sitt eller ligg — aldri stå. Prikking og lett svimmelhet er ventet og trygt. Stopp umiddelbart ved bevissthetstap.',
      en: 'Do not use with cardiovascular conditions, pregnancy, or epilepsy. Sit or lie down — never stand. Tingling and mild lightheadedness are expected and safe. Stop immediately if consciousness fades.',
    },
    closingNote: {
      no: 'Aktiveringen du kjente var det tilsiktede svaret, ikke et faresignal.',
      en: 'The activation you felt was the intended response, not a warning signal.',
    },
  },

  coherence: {
    id: 'coherence',
    name: { no: 'Koherenspusten', en: 'The Coherence Breath' },
    subtitle: {
      no: 'Likt 5-5 pust gir 6 pust i minuttet — hjertets 0,1 Hz koherensrytme.',
      en: 'Equal 5-5 breathing gives 6 breaths per minute — the heart’s 0.1 Hz coherence rhythm.',
    },
    durationLabel: { no: '10 minutter', en: '10 minutes' },
    phases: [
      {
        kind: 'inhale',
        seconds: 5,
        label: IN,
        instruction: {
          no: 'Gjennom nesen. Legg den dominante hånden på hjertet og rett oppmerksomheten dit. Pust inn i hjerterommet.',
          en: 'Through the nose. Place your dominant hand on your heart and direct attention there. Breathe into the heart space.',
        },
      },
      {
        kind: 'exhale',
        seconds: 5,
        label: OUT,
        instruction: {
          no: 'Gjennom nesen. Behold oppmerksomheten på hjertet. Det likt forholdet er avgjørende for entrainment.',
          en: 'Through the nose. Keep attention on the heart. The equal ratio is critical for entrainment.',
        },
      },
    ],
    durationSec: 600,
    sound: { carrierHz: 528, beatHz: 8 },
    closingNote: {
      no: 'Legg til ekte takknemlighet eller omsorg for noe du er glad i. Denne følelsen gir dypere koherens enn pust alene.',
      en: 'Add genuine appreciation or care for something you love. This feeling produces deeper coherence than breath alone.',
    },
  },

  void: {
    id: 'void',
    name: { no: 'Tomhetspusten', en: 'The Void Breath' },
    subtitle: {
      no: '4-7-8 med tom pause — tar nervesystemet til theta-grensen.',
      en: '4-7-8 with an empty pause — takes the nervous system to the theta boundary.',
    },
    durationLabel: { no: '12 minutter', en: '12 minutes' },
    phases: [
      {
        kind: 'inhale',
        seconds: 4,
        label: IN,
        instruction: {
          no: 'Kun nese. Sakte. Kun magen — hold brystet helt stille.',
          en: 'Nose only. Slow. Belly exclusively — keep the chest completely still.',
        },
      },
      {
        kind: 'holdIn',
        seconds: 7,
        label: HOLD,
        instruction: {
          no: 'Mildt. Ingen anstrengelse. La bevisstheten utvide seg inn i pausens rom.',
          en: 'Gentle. No strain. Let awareness expand into the space of the pause.',
        },
      },
      {
        kind: 'exhale',
        seconds: 8,
        label: OUT,
        instruction: {
          no: 'Fullstendig og sakte gjennom munnen.',
          en: 'Complete and slow through the mouth.',
        },
      },
      {
        kind: 'holdOut',
        seconds: 4,
        label: EMPTY,
        instruction: {
          no: 'Hold tom i 4 tellinger før neste innpust. Denne null-pusten er overgangspunktet — grensen kan løses opp her.',
          en: 'Hold empty for 4 counts before the next inhale. This zero-breath is the transition point — boundaries may dissolve here.',
        },
      },
    ],
    cycles: 6,
    settle: {
      seconds: 300,
      instruction: {
        no: 'Slipp helt inn i naturlig pust i 5 minutter uten styring. Ikke rett eller mål — bare motta det som kommer.',
        en: 'Release completely into natural breath for 5 minutes without guidance. Do not direct or measure — simply receive.',
      },
    },
    sound: { carrierHz: 852, beatHz: 5, settleBeatHz: 4 },
    closingNote: {
      no: 'Denne ustyrte perioden er der tilstanden oftest festner seg.',
      en: 'This unguided period is where the state most often consolidates.',
    },
  },

  return: {
    id: 'return',
    name: { no: 'Tilbakekomsten', en: 'The Return' },
    subtitle: {
      no: 'Et retningssignal når tilstanden har kollapset i hverdagen. 90 sekunder.',
      en: 'A direction signal when the state has collapsed in daily life. 90 seconds.',
    },
    durationLabel: { no: '90 sekunder', en: '90 seconds' },
    phases: [
      {
        kind: 'inhale',
        seconds: 4,
        label: IN,
        instruction: {
          no: 'Gjennom nesen. Normal dybde.',
          en: 'Through the nose. Normal depth.',
        },
      },
      {
        kind: 'holdIn',
        seconds: 2,
        label: HOLD,
        instruction: {
          no: 'Kort. Akkurat lenge nok til å registrere pausen bevisst.',
          en: 'Brief. Just long enough to register the pause consciously.',
        },
      },
      {
        kind: 'exhale',
        seconds: 6,
        label: OUT,
        instruction: {
          no: 'Gjennom munnen. Bruk den indre setningen «Jeg vender tilbake» som en retningsinstruksjon.',
          en: 'Through the mouth. Use the inner phrase “I am returning” as a directional instruction.',
        },
      },
    ],
    cycles: 5,
    sound: { carrierHz: 396, beatHz: 10 },
    closingNote: {
      no: 'Kan gjøres stående, sittende eller gående. Ankeret styrkes bare med gjentakelse — bruk det så ofte du trenger.',
      en: 'Can be done standing, sitting, or walking. The anchor only strengthens with repetition — use it as often as needed.',
    },
  },
};

export const ROUTINES: Record<'morning' | 'evening', Routine> = {
  morning: {
    id: 'morning',
    name: { no: 'Morgenrutine', en: 'Morning routine' },
    description: {
      no: 'Hele sekvensen — Nedstigningen, Forsterkeren, Koherens og Tomhetspusten.',
      en: 'The full sequence — Descent, Amplifier, Coherence, and Void Breath.',
    },
    protocolIds: ['descent', 'pranic', 'coherence', 'void'],
  },
  evening: {
    id: 'evening',
    name: { no: 'Kveldsrutine', en: 'Evening routine' },
    description: {
      no: 'Samme fulle sekvens for å lande dagen mot theta-terskelen og søvn.',
      en: 'The same full sequence to land the day toward the theta threshold and sleep.',
    },
    protocolIds: ['descent', 'pranic', 'coherence', 'void'],
  },
};

export function getProtocol(id: string): Protocol | undefined {
  return PROTOCOLS[id];
}
